import React, { useCallback, useEffect, useState } from 'react';
import { IDialogProps } from '../../../lib/dialog/Dialog';
import { EditorService } from '../../../services/editor';
import {
	EntityId,
	EntityType,
	InputMapState,
} from '@elyx-code/project-logic-tree';
import { useParams } from 'react-router-dom';
import InputMap from './InputMap';
import DialogLoader from '../../DialogLoader';
import { popupNotification } from '../../../popup-notification';
import EntityDialogHeader from '../../EntityDialogHeader';

export interface IInputMapDialogContentProps extends IDialogProps {
	project: EditorService;
	ready: boolean;
}

export interface IInputMapSectionProps {
	entity: InputMapState;
	project: EditorService;
}

const InputMapDialogContent: React.FC<IInputMapDialogContentProps> = ({
	onClose,
	project,
	ready,
}) => {
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: InputMapState | null =
		(project?.logic?.get(entityId) as InputMapState | null) || null;

	const onEscapePressed = useCallback(
		(e: KeyboardEvent) => {
			// Or close the dialog if there is only one tab
			onClose(e);
		},
		[onClose]
	);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('escape-key-press', onEscapePressed);

		if (!!entity) {
			project?.on(entity.id, () => {
				forceUpdate();
			});
		}

		return () => {
			project?.off('escape-key-press', onEscapePressed);
		};
	}, [project, entity, onEscapePressed]);

	if (!project || !entity) {
		if (!!project && project.hasLoadedLogic && !entity) {
			popupNotification({
				color: 'error',
				text: 'This input does not exist in the current version of the project',
			});
			onClose(null);
			return;
		}

		return (
			<DialogLoader
				title='Input'
				subtitle='Input builder'
				onClose={onClose}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				paddingBottom: 20,
			}}
		>
			<EntityDialogHeader entity={entity} project={project} onClose={onClose} />

			<div
				style={{
					display: 'flex',
					flexGrow: 1,
					overflowY: 'scroll',
				}}
			>
				{/* We need the 'span' for the react ref */}
				<span
					style={{
						maxHeight: '100%',
						maxWidth: '100%',
						flexGrow: 1,
						height: '100%',
					}}
				>
					<InputMap
						entity={entity}
						project={project}
						onClose={(e) => {
							onClose(e);
						}}
					/>
				</span>
			</div>
		</div>
	);
};

export default InputMapDialogContent;
